import { Component, OnInit, OnDestroy, inject, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AppSupportService } from '../../services/app-support.service';
import { User } from '../../models/user.model';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-tag';
import { TagWithOverflowComponent } from '../../shared-components/tag-with-overflow/tag-with-overflow.component';
import { AuthPolicies } from '../../auth/auth-policies';
import { MasterDataAuditLogComponent } from '../../shared-components/master-data-audit-log/master-data-audit-log.component';

@Component({
  selector: 'app-user-details',
  standalone: true,
  imports: [CommonModule, RouterModule, TagWithOverflowComponent, MasterDataAuditLogComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './user-details.component.html',
  styleUrls: ['./user-details.component.scss'],
})
export class UserDetailsComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly appSupportService = inject(AppSupportService);
  readonly authPolicy = inject(AuthPolicies);
  private readonly destroyed$ = new Subject<void>();

  user?: User;
  loading = true;
  activeTab = 0;
  openAuditLog = false;

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    this.activeTab = this.route.snapshot.queryParamMap.get('tab') === 'non-shop-user' ? 1 : 0;
    if (id) {
      this.getUserDetails(id);
    } else {
      this.loading = false;
    }
  }

  // =========================
  // LOAD USER
  // =========================

  getUserDetails(userId: number): void {
    this.appSupportService
      .getUserDetailsBasedOnUserName(userId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res) => {
          this.user = res;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  // =========================
  // EDIT ROUTING
  // =========================

  onEditClicked(): void {
    if (this.user?.userId == null) {
      return;
    }
    this.router.navigate(['/users/edit', this.user.userId], {
      queryParams: { tab: this.isShopUser ? 'shop-user' : 'non-shop-user' },
    });
  }

  // =========================
  // AUDIT ROUTING (Optional)
  // =========================

  onAuditLogClicked(): void {
    if (this.user?.userId == null) {
      return;
    }
    this.openAuditLog = true;
  }

  onAuditLogClosed(): void {
    this.openAuditLog = false;
  }

  readonly auditLogFetcher = (transactionCode: string, isSystemLog: boolean, page: number, limit: number) =>
    this.appSupportService.getUserAuditLogs(transactionCode, isSystemLog, page, limit);

  // =========================
  // COMPUTED HELPERS
  // =========================

  get isActive(): boolean {
    return this.user ? this.user.isActive : false;
  }

  get isShopUser(): boolean {
    return this.user?.roles?.some((r) => r.toLowerCase() === 'shop') ?? false;
  }

  get backLink(): string {
    return this.activeTab === 1 ? '/users?tab=1' : '/users';
  }

  get roleTagData(): string[] {
    return this.user?.roles?.length ? this.user.roles : [];
  }

  get regionTagData(): string[] {
    return this.user?.regions?.length ? this.user.regions.map((r) => (r.name ?? r.code ?? '').trim()).filter(Boolean) : [];
  }

  get shopTagData(): string[] {
    return this.user?.maintenanceShops?.length ? [...new Set(this.user.maintenanceShops.map((s) => s.maintenanceShopCode))] : [];
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
